import React, { useCallback, useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Card, Row, Col, Alert, message, Skeleton } from 'antd';
import {
  fetchTransactionLogsSummary,
  fetchTransactionLogs,
  type TransactionLogSummary,
  type TransactionLogsFiltersValues,
  type TransactionLogsSummary,
} from '../../services/transactionLogsService';
import { TransactionLogsFilters } from './components/TransactionLogsFilters';
import { TransactionLogsTable } from './components/TransactionLogsTable';
import { TransactionLogDetailDrawer } from './components/TransactionLogDetailDrawer';
import {
  TransactionLogSummaryCard,
  formatSummaryValue,
} from './components/TransactionLogSummaryCard';
import { EmptyState } from '../../design-system/EmptyState';
import { spacing } from '../../design-system/tokens';
import { formatVnCalendarDate, vnDayjs } from '../../utils/vnDateTime';

const ROUNDED_CARD_STYLE: React.CSSProperties = {
  borderRadius: 16,
  boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
};

const PAGE_SIZE = 20;
const DEFAULT_DAYS = 7;

function getDefaultDateRange(): { from: string; to: string } {
  const end = vnDayjs();
  const start = end.subtract(DEFAULT_DAYS - 1, 'day');
  return {
    from: start.format('YYYY-MM-DD'),
    to: end.format('YYYY-MM-DD'),
  };
}

function getDateRangeSubtitle(from?: string, to?: string): string {
  if (from && to) {
    return `${formatVnCalendarDate(from)} - ${formatVnCalendarDate(to)}`;
  }
  return `${DEFAULT_DAYS} ngày gần nhất`;
}

export default function TransactionLogsListPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const defaultRange = getDefaultDateRange();

  const [summary, setSummary] = useState<TransactionLogsSummary | null>(null);
  const [summaryLoading, setSummaryLoading] = useState(true);
  const [logs, setLogs] = useState<TransactionLogSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(() =>
    parseInt(searchParams.get('page') || '1', 10)
  );
  const [pageSize, setPageSize] = useState(() =>
    parseInt(searchParams.get('pageSize') || String(PAGE_SIZE), 10)
  );
  const [total, setTotal] = useState(0);
  const [detailId, setDetailId] = useState<number | null>(null);
  const [detailOpen, setDetailOpen] = useState(false);

  const [filters, setFilters] = useState<TransactionLogsFiltersValues>(() => {
    const from = searchParams.get('from') || searchParams.get('dateFrom');
    const to = searchParams.get('to') || searchParams.get('dateTo');
    return {
      q: searchParams.get('q') || searchParams.get('keyword') || undefined,
      from: from || defaultRange.from,
      to: to || defaultRange.to,
      transactionType:
        (searchParams.get(
          'transactionType'
        ) as TransactionLogsFiltersValues['transactionType']) || undefined,
      paymentMethod:
        (searchParams.get(
          'paymentMethod'
        ) as TransactionLogsFiltersValues['paymentMethod']) || undefined,
      internalStatus:
        (searchParams.get(
          'internalStatus'
        ) as TransactionLogsFiltersValues['internalStatus']) || undefined,
      gatewayStatus:
        (searchParams.get(
          'gatewayStatus'
        ) as TransactionLogsFiltersValues['gatewayStatus']) || undefined,
      merchantId: searchParams.get('merchantId')
        ? parseInt(searchParams.get('merchantId')!, 10)
        : undefined,
      amountMin: searchParams.get('amountMin')
        ? parseInt(searchParams.get('amountMin')!, 10)
        : undefined,
      amountMax: searchParams.get('amountMax')
        ? parseInt(searchParams.get('amountMax')!, 10)
        : undefined,
      hasWebhook:
        searchParams.get('hasWebhook') === 'true'
          ? true
          : searchParams.get('hasWebhook') === 'false'
          ? false
          : undefined,
      failedOnly: searchParams.get('failedOnly') === 'true',
      refundedOnly: searchParams.get('refundedOnly') === 'true',
    };
  });

  const syncUrl = useCallback(
    (params: Record<string, string | number | boolean | undefined>) => {
      const next = new URLSearchParams();
      if (params.page && params.page > 1) next.set('page', String(params.page));
      if (params.limit && params.limit !== PAGE_SIZE)
        next.set('pageSize', String(params.limit));
      if (params.q) next.set('q', String(params.q));
      if (params.from) next.set('from', String(params.from));
      if (params.to) next.set('to', String(params.to));
      if (params.transactionType)
        next.set('transactionType', String(params.transactionType));
      if (params.paymentMethod)
        next.set('paymentMethod', String(params.paymentMethod));
      if (params.internalStatus)
        next.set('internalStatus', String(params.internalStatus));
      if (params.gatewayStatus)
        next.set('gatewayStatus', String(params.gatewayStatus));
      if (params.merchantId != null)
        next.set('merchantId', String(params.merchantId));
      if (params.amountMin != null)
        next.set('amountMin', String(params.amountMin));
      if (params.amountMax != null)
        next.set('amountMax', String(params.amountMax));
      if (params.hasWebhook != null)
        next.set('hasWebhook', String(params.hasWebhook));
      if (params.failedOnly) next.set('failedOnly', String(params.failedOnly));
      if (params.refundedOnly)
        next.set('refundedOnly', String(params.refundedOnly));
      setSearchParams(next, { replace: true });
    },
    [setSearchParams]
  );

  const loadSummary = useCallback(
    async (overrides?: { from?: string; to?: string }) => {
      setSummaryLoading(true);
      try {
        const from = overrides?.from ?? filters.from;
        const to = overrides?.to ?? filters.to;
        const res = await fetchTransactionLogsSummary({ from, to });
        if (res.success && res.data) setSummary(res.data);
      } catch {
        setSummary(null);
      } finally {
        setSummaryLoading(false);
      }
    },
    [filters.from, filters.to]
  );

  const loadLogs = useCallback(
    async (overrides?: {
      page?: number;
      pageSize?: number;
      filtersOverride?: TransactionLogsFiltersValues;
    }) => {
      setLoading(true);
      setError(null);
      const effectiveFilters = overrides?.filtersOverride ?? filters;
      const params: Record<string, string | number | boolean | undefined> = {
        page: overrides?.page ?? page,
        limit: overrides?.pageSize ?? pageSize,
      };
      if (effectiveFilters.q) params.q = effectiveFilters.q;
      if (effectiveFilters.from) params.from = effectiveFilters.from;
      if (effectiveFilters.to) params.to = effectiveFilters.to;
      if (effectiveFilters.transactionType)
        params.transactionType = effectiveFilters.transactionType;
      if (effectiveFilters.paymentMethod)
        params.paymentMethod = effectiveFilters.paymentMethod;
      if (effectiveFilters.internalStatus)
        params.internalStatus = effectiveFilters.internalStatus;
      if (effectiveFilters.gatewayStatus)
        params.gatewayStatus = effectiveFilters.gatewayStatus;
      if (effectiveFilters.merchantId != null)
        params.merchantId = effectiveFilters.merchantId;
      if (effectiveFilters.amountMin != null)
        params.amountMin = effectiveFilters.amountMin;
      if (effectiveFilters.amountMax != null)
        params.amountMax = effectiveFilters.amountMax;
      if (effectiveFilters.hasWebhook != null)
        params.hasWebhook = effectiveFilters.hasWebhook;
      if (effectiveFilters.failedOnly)
        params.failedOnly = effectiveFilters.failedOnly;
      if (effectiveFilters.refundedOnly)
        params.refundedOnly = effectiveFilters.refundedOnly;
      try {
        const res = await fetchTransactionLogs(
          params as Parameters<typeof fetchTransactionLogs>[0]
        );
        if (res.success && res.data) {
          setLogs(res.data);
          setTotal(res.pagination?.total ?? 0);
          syncUrl(params);
        } else {
          setLogs([]);
          setTotal(0);
          setError(res.message || 'Không tải được danh sách');
        }
      } catch (e: unknown) {
        const ax = e as {
          response?: { data?: { message?: string } };
          message?: string;
        };
        setError(
          ax.response?.data?.message ||
            ax.message ||
            'Lỗi mạng. Vui lòng thử lại.'
        );
        message.error('Không tải được danh sách giao dịch');
        setLogs([]);
        setTotal(0);
      } finally {
        setLoading(false);
      }
    },
    [filters, page, pageSize, syncUrl]
  );

  useEffect(() => {
    loadSummary();
  }, [loadSummary]);

  useEffect(() => {
    loadLogs();
  }, [loadLogs]);

  const handleSearch = () => {
    setPage(1);
    loadSummary();
    loadLogs({ page: 1 });
  };

  const handleReset = () => {
    const def = getDefaultDateRange();
    const newFilters: TransactionLogsFiltersValues = {
      q: undefined,
      from: def.from,
      to: def.to,
      transactionType: undefined,
      paymentMethod: undefined,
      internalStatus: undefined,
      gatewayStatus: undefined,
      merchantId: undefined,
      amountMin: undefined,
      amountMax: undefined,
      hasWebhook: undefined,
      failedOnly: false,
      refundedOnly: false,
    };
    setFilters(newFilters);
    setPage(1);
    setSearchParams({}, { replace: true });
    loadSummary({ from: def.from, to: def.to });
    loadLogs({ page: 1, filtersOverride: newFilters });
  };

  const handlePageChange = (p: number, size?: number) => {
    setPage(p);
    if (size != null) setPageSize(size);
    loadLogs(size != null ? { page: p, pageSize: size } : { page: p });
  };

  const handleViewDetail = (record: TransactionLogSummary) => {
    setDetailId(record.id);
    setDetailOpen(true);
  };

  const dateSubtitle = getDateRangeSubtitle(filters.from, filters.to);

  return (
    <div
      style={{
        padding: spacing.lg,
        maxWidth: 1600,
        margin: '0 auto',
      }}
    >
      <header className="merchant-detail-header">
        <h1 className="merchant-detail-title">Transaction Logs</h1>
        <nav className="merchant-detail-breadcrumb">
          <Link to="/">Dashboard</Link>
          <span className="merchant-detail-breadcrumb-sep">&gt;</span>
          <span className="merchant-detail-breadcrumb-current">
            Nhật ký giao dịch
          </span>
        </nav>
      </header>

      <p style={{ color: '#8c8c8c', marginBottom: spacing.lg, fontSize: 14 }}>
        Theo dõi thanh toán và hoàn tiền trên nền tảng
      </p>

      <Row gutter={[16, 16]} style={{ marginBottom: spacing.lg }}>
        <Col xs={24} sm={12} md={8} lg={4}>
          <TransactionLogSummaryCard
            type="total"
            title="Tổng giao dịch"
            value={formatSummaryValue('total', summary?.total ?? 0)}
            subtitle={dateSubtitle}
            loading={summaryLoading}
          />
        </Col>
        <Col xs={24} sm={12} md={8} lg={4}>
          <TransactionLogSummaryCard
            type="success"
            title="Thanh toán thành công"
            value={formatSummaryValue('success', summary?.success ?? 0)}
            subtitle={dateSubtitle}
            loading={summaryLoading}
          />
        </Col>
        <Col xs={24} sm={12} md={8} lg={4}>
          <TransactionLogSummaryCard
            type="failed"
            title="Thanh toán thất bại"
            value={formatSummaryValue('failed', summary?.failed ?? 0)}
            subtitle={dateSubtitle}
            loading={summaryLoading}
          />
        </Col>
        <Col xs={24} sm={12} md={8} lg={4}>
          <TransactionLogSummaryCard
            type="refunded"
            title="Hoàn tiền"
            value={formatSummaryValue('refunded', summary?.refunded ?? 0)}
            subtitle={dateSubtitle}
            loading={summaryLoading}
          />
        </Col>
        <Col xs={24} sm={12} md={8} lg={4}>
          <TransactionLogSummaryCard
            type="volume"
            title="Tổng giá trị giao dịch"
            value={formatSummaryValue('volume', summary?.volume ?? 0)}
            subtitle={dateSubtitle}
            loading={summaryLoading}
          />
        </Col>
        <Col xs={24} sm={12} md={8} lg={4}>
          <TransactionLogSummaryCard
            type="pending"
            title="Pending quá 15 phút"
            value={formatSummaryValue(
              'pending',
              summary?.pendingOver15Min ?? 0
            )}
            subtitle="Theo bộ lọc hiện tại"
            loading={summaryLoading}
          />
        </Col>
      </Row>

      <Card style={ROUNDED_CARD_STYLE} bodyStyle={{ padding: spacing.lg }}>
        <TransactionLogsFilters
          values={filters}
          onChange={setFilters}
          onSearch={handleSearch}
          onReset={handleReset}
          loading={loading}
        />

        <div style={{ marginTop: spacing.xl }}>
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: spacing.md,
              flexWrap: 'wrap',
              gap: 8,
            }}
          >
            <h3 style={{ margin: 0, fontSize: 16, fontWeight: 600 }}>
              Danh sách giao dịch
              {total > 0 && (
                <span
                  style={{
                    marginLeft: 8,
                    fontSize: 14,
                    fontWeight: 400,
                    color: '#8c8c8c',
                  }}
                >
                  ({total} kết quả)
                </span>
              )}
            </h3>
          </div>

          {error && (
            <Alert
              type="error"
              message={error}
              showIcon
              action={
                <span
                  onClick={() => loadLogs()}
                  style={{ cursor: 'pointer', textDecoration: 'underline' }}
                >
                  Thử lại
                </span>
              }
              style={{ marginBottom: spacing.md }}
            />
          )}

          {!error && logs.length === 0 && !loading && (
            <EmptyState
              description="Chưa có giao dịch nào"
              onRefresh={() => loadLogs()}
              onClearFilters={handleReset}
            />
          )}

          {!error && (logs.length > 0 || loading) && (
            <TransactionLogsTable
              data={logs}
              loading={loading}
              page={page}
              pageSize={pageSize}
              total={total}
              onPageChange={handlePageChange}
              onViewDetail={handleViewDetail}
            />
          )}
        </div>
      </Card>

      <TransactionLogDetailDrawer
        id={detailId}
        open={detailOpen}
        onClose={() => {
          setDetailOpen(false);
          setDetailId(null);
        }}
        onRefresh={() => loadLogs()}
      />
    </div>
  );
}
